import React, { useEffect, useState } from "react";
import {
  EAutoExposureMethod,
  EDropLocationMethod,
  ELightPath,
  ImagingCaptureProfile,
} from "../../Model/CaptureProfile";
import {
  FilterCube,
  LightPathCapability,
  CropZoom,
} from "../../Model/ImagerCapabilities";
import Modal from "../Common/Modal";
import "./ImagerFeatureControlLoader.css";
import CommonDropDown from "../Common/DropDown/CommonDropDown";

const getFormUnit = (
  label: string,
  value: string | number | null | undefined,
  unit: string
) => {
  return (
    <div className="load-unit">
      <label>{label}</label>
      <input value={value != null ? value + " " + unit : ""} disabled={true} />
    </div>
  );
};

type Props = {
  captureProfiles: ImagingCaptureProfile[];
  setCaptureProfile: (captureprofile: ImagingCaptureProfile) => void;
  lightPathCapability: LightPathCapability;
  lightPath: ELightPath;
  showPopUp: boolean;
  setShowPopUp: (show: boolean) => void;
};

const initialState: JSX.Element[] = [<div />];

export default function ImagerFeatureControlLoaderPopUp({
  captureProfiles,
  setCaptureProfile,
  lightPathCapability,
  lightPath,
  showPopUp,
  setShowPopUp,
}: Props) {
  const [selectedCaptureProfile, setSelectedCaptureProfile] = useState(
    captureProfiles.length > 0 ? captureProfiles[0] : null
  );
  const [settingForm, setSettingForm] = useState(initialState);

  const onClickOk = () => {
    if (selectedCaptureProfile != null) {
      setCaptureProfile(selectedCaptureProfile);
      setShowPopUp(false);
    }
  };

  const onClickCancel = () => {
    setShowPopUp(false);
  };

  const onChangeCaptureProfile = (captureProfileId: string): void => {
    const cps = captureProfiles.filter(
      (cp) => cp.captureProfileId === captureProfileId
    );
    if (cps.length > 0) {
      setSelectedCaptureProfile(cps[0]);
    }
  };

  // create Capture Profile DisplayValues for drop down menu
  const captureProfileDv = captureProfiles.map((captureProfile) => ({
    display: captureProfile.name,
    value: captureProfile.captureProfileId,
    disabled: false,
  }));

  // change the Form when state selectedCP changed based on lightpath
  useEffect(() => {
    if (selectedCaptureProfile != null) {
      //-------Optic Capabilities------//

      const continuousZoom = lightPathCapability.optic.continuousZoom;
      const objectiveZoom = lightPathCapability.optic.objectiveZoom;
      const cropZoom = lightPathCapability.optic.cropZoom;
      const condenser = lightPathCapability.optic.condenser;
      const polarizer = lightPathCapability.optic.polarizer;
      const brightness = lightPathCapability.optic.brightness;
      const filterCubes = lightPathCapability.optic.filterCubes;

      //-------Camera Capabilities------//

      const exposure = lightPathCapability.camera.exposure;
      const gamma = lightPathCapability.camera.gamma;
      const gain = lightPathCapability.camera.gain;
      const binning = lightPathCapability.camera.binning;

      let disabledForm: JSX.Element[] = [];
      switch (lightPath) {
        case ELightPath.Visible: {
          const visCp = selectedCaptureProfile.visible;
          if (visCp == null) {
            return;
          }
          if (
            continuousZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(getFormUnit("Zoom", visCp.continuousZoom, "X"));
          if (
            objectiveZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(
              getFormUnit("Objective", visCp.objectiveZoom, "X")
            );

          if (condenser)
            disabledForm.push(getFormUnit("Condenser", visCp.condenser, "%"));
          if (polarizer)
            disabledForm.push(getFormUnit("Polarizer", visCp.polarizer, "%"));
          if (
            brightness &&
            visCp.autoExposure?.autoExposureMethod === EAutoExposureMethod.Fixed
          )
            disabledForm.push(getFormUnit("Brightness", visCp.brightness, "%"));
          if (
            exposure &&
            visCp.autoExposure?.autoExposureMethod === EAutoExposureMethod.Fixed
          )
            disabledForm.push(getFormUnit("Exposure", visCp.exposure, " ms"));
          if (gamma) disabledForm.push(getFormUnit("Gamma", visCp.gamma, ""));
          if (gain) disabledForm.push(getFormUnit("Gain", visCp.gain, "dB"));
          if (binning)
            disabledForm.push(
              getFormUnit(
                "Binning",
                visCp.binning + "x" + visCp.binning,
                "binning"
              )
            );
          break;
        }
        case ELightPath.Fluorescence:
          const flCp = selectedCaptureProfile.fluorescence;

          if (flCp == null) {
            return;
          }

          const visFlCp = flCp.visibleFluorescence;
          const uvFlCp = flCp.ultravioletFluorescence;

          if (visFlCp == null) {
            throw new Error("Visible fluorescence capture profile is null");
          }

          if (uvFlCp == null) {
            throw new Error("Ultraviolet fluorescence capture profile is null");
          }

          if (filterCubes == null) {
            throw new Error("Filter cubes is null");
          }

          const selectedFilterCube = filterCubes.filterCubes.filter(
            (fc) => fc.id === flCp.filterCubeId
          )[0];
          const isVisibleFluorescence = selectedFilterCube.type === 2;

          disabledForm.push(
            getFormUnit(
              "Excitation",
              filterCubes.filterCubes.filter(
                (fc: FilterCube) => fc.id === flCp.filterCubeId
              )[0].name,
              ""
            )
          );
          if (
            objectiveZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(
              getFormUnit("Objective", flCp.objectiveZoom, "X")
            );
          if (
            cropZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(getFormUnit("Crop Zoom", flCp.cropZoom, "X"));
          if (brightness && isVisibleFluorescence)
            disabledForm.push(
              getFormUnit("Brightness", visFlCp.brightness, "%")
            );
          if (exposure && isVisibleFluorescence)
            disabledForm.push(getFormUnit("Exposure", visFlCp.exposure, "ms"));
          if (exposure && !isVisibleFluorescence)
            disabledForm.push(getFormUnit("Exposure", uvFlCp.exposure, "ms"));
          if (gamma)
            disabledForm.push(
              getFormUnit(
                "Gamma",
                isVisibleFluorescence ? visFlCp.gamma : uvFlCp.gamma,
                ""
              )
            );
          if (gain)
            disabledForm.push(
              getFormUnit(
                "Gain",
                isVisibleFluorescence ? visFlCp.gain : uvFlCp.gain,
                "dB"
              )
            );
          if (binning)
            disabledForm.push(
              getFormUnit(
                "Binning",
                flCp.binning + "x" + flCp.binning,
                "binning"
              )
            );
          break;
        case ELightPath.Ultraviolet:
          const uvCp = selectedCaptureProfile.ultraviolet;

          if (uvCp === null) {
            return;
          }

          if (
            objectiveZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(
              getFormUnit("Objective", uvCp?.objectiveZoom, "X")
            );
          if (
            cropZoom &&
            selectedCaptureProfile.dropLocation?.dropLocationMethod ===
              EDropLocationMethod.Fixed
          )
            disabledForm.push(getFormUnit("Crop Zoom", uvCp?.cropZoom, "X"));
          if (exposure)
            disabledForm.push(getFormUnit("Exposure", uvCp?.exposure, "ms"));
          if (gamma) disabledForm.push(getFormUnit("Gamma", uvCp?.gamma, ""));
          if (gain) disabledForm.push(getFormUnit("Gain", uvCp?.gain, "dB"));
          if (binning)
            disabledForm.push(
              getFormUnit(
                "Binning",
                uvCp?.binning + "x" + uvCp?.binning,
                " binning"
              )
            );
          break;
        default:
          break;
      }
      setSettingForm(disabledForm);
    }
  }, [selectedCaptureProfile, lightPathCapability, lightPath]);

  return (
    <Modal
      show={showPopUp}
      onHide={onClickCancel}
      className={"load-settings-modal-main"}
    >
      <Modal.Header
        hideButtonEnabled={true}
        title="Load Settings"
      ></Modal.Header>

      <Modal.Body>
        <div className="load-selector">
          <label>Imaging Setting</label>
          <CommonDropDown
            id="imaging-settings-selector"
            onChange={(value) => onChangeCaptureProfile(value)}
            disabled={captureProfiles.length === 0}
            value={
              selectedCaptureProfile
                ? selectedCaptureProfile?.captureProfileId
                : ""
            }
            dropDownDisplayValues={captureProfileDv}
            style={{
              height: 32,
              fontSize: 14,
              width: "350px"
            }}
          />
        </div>
        <div className="load-body">
          <label>
            {selectedCaptureProfile == null
              ? "No imaging setting defined"
              : "Setting"}
          </label>
          {settingForm}
        </div>
      </Modal.Body>
      <Modal.Footer
        onClickOk={onClickOk}
        onClickCancel={onClickCancel}
        okButtonDisabled={selectedCaptureProfile == null}
      />
    </Modal>
  );
}
